import React from 'react';
import { 
  Newspaper,
  Check,
  AlertCircle,
  Clock,
  ArrowUpRight,
  RefreshCcw
} from 'lucide-react';

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children }) => (
  <div className="border-b border-gray-200 p-4 sm:p-6">{children}</div>
);

const CardContent = ({ children }) => (
  <div className="p-4 sm:p-6">{children}</div>
);

const SourceItem = ({ name, status, lastChecked, type }) => {
  const statusColors = {
    active: 'bg-green-50 text-green-700',
    pending: 'bg-yellow-50 text-yellow-700',
    error: 'bg-red-50 text-red-700'
  };

  const statusIcons = {
    active: Check,
    pending: Clock,
    error: AlertCircle
  };

  const Icon = statusIcons[status];

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          <Newspaper className="h-5 w-5 text-gray-400" />
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-900">{name}</h4>
          <p className="text-xs text-gray-500">{type}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-xs text-gray-500">
          Last checked: {lastChecked}
        </span>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status]}`}>
          <Icon className="w-3 h-3 mr-1" />
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>
    </div>
  );
};

const DealNewsMonitor = () => {
  const sources = [
    {
      name: "Bisnow",
      type: "Real Estate News",
      status: "active",
      lastChecked: "5m ago"
    },
    {
      name: "CRE Daily",
      type: "Industry News",
      status: "active",
      lastChecked: "10m ago"
    },
    {
      name: "The Real Deal",
      type: "Market Intelligence",
      status: "active",
      lastChecked: "15m ago"
    },
    {
      name: "PitchBook",
      type: "Deal Database",
      status: "active",
      lastChecked: "20m ago"
    },
    {
      name: "Preqin",
      type: "Investment Data",
      status: "active",
      lastChecked: "25m ago"
    },
    {
      name: "Commercial Observer",
      type: "Industry News",
      status: "active",
      lastChecked: "30m ago"
    },
    {
      name: "LinkedIn",
      type: "Professional Network",
      status: "active",
      lastChecked: "5m ago"
    },
    {
      name: "Pincus",
      type: "Real Estate News",
      status: "active",
      lastChecked: "35m ago"
    }
  ];

  const stats = {
    totalSources: sources.length,
    activeSources: sources.filter(s => s.status === 'active').length,
    dealsCaptured: 127,
    lastUpdate: '2 minutes ago'
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Newspaper className="h-5 w-5 text-gray-400" />
            <h3 className="text-base font-semibold text-gray-900">Deal News Monitor</h3>
          </div>
          <button className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <dt className="text-sm font-medium text-blue-600">Total Sources</dt>
            <dd className="mt-1 text-2xl font-semibold text-blue-700">{stats.totalSources}</dd>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <dt className="text-sm font-medium text-green-600">Active Sources</dt>
            <dd className="mt-1 text-2xl font-semibold text-green-700">{stats.activeSources}</dd>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <dt className="text-sm font-medium text-purple-600">Deals Captured</dt>
            <dd className="mt-1 text-2xl font-semibold text-purple-700">{stats.dealsCaptured}</dd>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <dt className="text-sm font-medium text-gray-600">Last Update</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-700">{stats.lastUpdate}</dd>
          </div>
        </div>

        {/* Source List */}
        <div className="space-y-1">
          {sources.map((source, idx) => (
            <SourceItem key={idx} {...source} />
          ))}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end gap-3">
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
            Configure Sources
          </button>
          <button className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
            View All Deals
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DealNewsMonitor;